import { isPlatformBrowser as isPlatformBrowser$1, CommonModule } from '@angular/common';
import { Injectable, NgZone, defineInjectable, inject, EventEmitter, Directive, Inject, PLATFORM_ID, ElementRef, Input, Output, NgModule } from '@angular/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/*******************************************************************************
 * @license
 * Copyright (c) 2020 Piotr Stępniewski <k3nsei.pl@gmail.com>
 * (https://www.linkedin.com/in/piotrstepniewski/)
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://opensource.org/licenses/MIT
 */
/** @enum {string} */
import * as ɵngcc0 from '@angular/core';
const InViewportConfigDirection = {
    BOTH: 'both',
    VERTICAL: 'vertical',
    HORIZONTAL: 'horizontal',
};
/**
 * @return {?}
 */
function isPlatformBrowser() {
    try {
        return typeof window !== 'undefined' && this === window;
    }
    catch (e) {
        return false;
    }
}
/**
 * @param {?} input
 * @return {?}
 */
function toBase64(input) {
    return !isPlatformBrowser()
        ? toBase64Node(input)
        : toBase64Browser(input);
}
/**
 * @param {?} input
 * @return {?}
 */
function toBase64Node(input) {
    try {
        return typeof global !== 'undefined' && global.Buffer.from(input).toString('base64');
    }
    catch (e) {
        return input;
    }
}
/**
 * @param {?} input
 * @return {?}
 */
function toBase64Browser(input) {
    try {
        return typeof window !== 'undefined' && window.btoa(input);
    }
    catch (e) {
        return input;
    }
}
class InViewportConfig {
    /**
     * @param {?=} options
     */
    constructor(options) {
        this._rootMargin = '0px 0px 0px 0px';
        this._threshold = [...InViewportConfig.DEFAULT_THRESHOLD];
        this._partial = true;
        this._direction = InViewportConfigDirection.BOTH;
        if (Object.prototype.toString.call(options) === '[object Object]') {
            ['root', 'rootMargin', 'threshold', 'partial', 'direction', 'checkFn'].forEach((prop) => {
                if (options.hasOwnProperty(prop)) {
                    this[prop] = options[prop];
                }
            });
        }
        this._hash = InViewportConfig.hash({
            rootMargin: this.rootMargin,
            threshold: this.threshold,
            partial: this.partial,
            direction: this.direction,
            checkFn: String(this.checkFn)
        });
    }
    /**
     * @private
     * @param {?} input
     * @return {?}
     */
    static stringify(input) {
        if (Array.isArray(input)) {
            /** @type {?} */
            const stringifiedArr = [];
            // tslint:disable-next-line:prefer-for-of
            for (let i = 0; i < input.length; i++) {
                stringifiedArr.push(InViewportConfig.stringify(input[i]));
            }
            return `[${stringifiedArr.join(',')}]`;
        }
        else if (typeof input === 'object' && input !== null) {
            /** @type {?} */
            const acc = [];
            /** @type {?} */
            const sortedKeys = Object.keys(input).sort();
            for (const k of sortedKeys) {
                /** @type {?} */
                const v = InViewportConfig.stringify(input[k]);
                acc.push(`${k}:${v}`);
            }
            return acc.join(InViewportConfig.STRINGIFY_DELIMITER);
        }
        return String(input);
    }
    /**
     * @private
     * @param {?} input
     * @return {?}
     */
    static hash(input) {
        return toBase64(InViewportConfig.stringify(input));
    }
    /**
     * @return {?}
     */
    get root() {
        return this._root;
    }
    /**
     * @param {?} value
     * @return {?}
     */
    set root(value) {
        this._root = value && value.nodeType === 1 ? value : undefined;
    }
    /**
     * @return {?}
     */
    get rootMargin() {
        return this._rootMargin;
    }
    /**
     * @param {?} value
     * @return {?}
     */
    set rootMargin(value) {
        if (!value || typeof value !== 'string') {
            this._rootMargin = '0px 0px 0px 0px';
        }
        else {
            /** @type {?} */
            const marginString = value || '0px';
            /** @type {?} */
            const margins = marginString.split(new RegExp('\\s+')).map((margin) => {
                /** @type {?} */
                const parts = /^(-?\d*\.?\d+)(px|%)$/.exec(margin);
                if (!parts) {
                    throw new TypeError('rootMargin must be specified in pixels or percent');
                }
                return `${parts[1]}${parts[2]}`;
            });
            margins[1] = margins[1] || margins[0];
            margins[2] = margins[2] || margins[0];
            margins[3] = margins[3] || margins[1];
            this._rootMargin = margins.join(' ');
        }
    }
    /**
     * @return {?}
     */
    get threshold() {
        return this._threshold;
    }
    /**
     * @param {?} value
     * @return {?}
     */
    set threshold(value) {
        /** @type {?} */
        let threshold = [];
        /** @type {?} */
        const isValidThreshold = (val) => typeof val === 'number' && val >= 0 && val <= 1;
        if (isValidThreshold(value)) {
            threshold = [value];
        }
        else if (Array.isArray(value) && value.length) {
            threshold = value.filter((val) => isValidThreshold(val));
        }
        if (threshold.length === 0) {
            threshold = [...InViewportConfig.DEFAULT_THRESHOLD];
        }
        this._threshold = threshold;
    }
    /**
     * @return {?}
     */
    get partial() {
        return this._partial;
    }
    /**
     * @param {?} value
     * @return {?}
     */
    set partial(value) {
        this._partial = !!value;
    }
    /**
     * @return {?}
     */
    get direction() {
        return this._direction;
    }
    /**
     * @param {?} value
     * @return {?}
     */
    set direction(value) {
        /** @type {?} */
        const isValidValue = (val) => {
            return ([
                InViewportConfigDirection.BOTH,
                InViewportConfigDirection.HORIZONTAL,
                InViewportConfigDirection.VERTICAL
            ].indexOf(val) >= 0);
        };
        this._direction = isValidValue(value) ? value : InViewportConfigDirection.BOTH;
    }
    /**
     * @return {?}
     */
    get hash() {
        return this._hash;
    }
    /**
     * @return {?}
     */
    get checkFn() {
        return this._checkFn;
    }
    /**
     * @param {?} value
     * @return {?}
     */
    set checkFn(value) {
        this._checkFn = value;
    }
}
InViewportConfig.DEFAULT_THRESHOLD = [0, 1];
InViewportConfig.STRINGIFY_DELIMITER = '|';

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class InViewportService {
    /**
     * @param {?} ngZone
     */
    constructor(ngZone) {
        this.ngZone = ngZone;
        this.trigger$ = new Subject();
        this.registry = [];
    }
    /**
     * @private
     * @param {?} entries
     * @return {?}
     */
    emitTrigger(entries) {
        if (Array.isArray(entries) && entries.length) {
            entries.forEach((entry) => this.trigger$.next(entry));
        }
    }
    /**
     * @private
     * @param {?=} element
     * @return {?}
     */
    getRootElement(element) {
        return element && element.nodeType === Node.ELEMENT_NODE ? element : null;
    }
    /**
     * @private
     * @param {?} root
     * @param {?} configHash
     * @return {?}
     */
    findEntry(root, configHash) {
        return this.registry.find((entry) => entry.root === this.getRootElement(root) && entry.configHash === configHash);
    }
    /**
     * @param {?} target
     * @param {?} config
     * @return {?}
     */
    register(target, config) {
        this.ngZone.runOutsideAngular(() => {
            /** @type {?} */
            const foundedEntry = this.findEntry(config.root, config.hash);
            if (foundedEntry && !foundedEntry.targets.has(target)) {
                foundedEntry.targets.add(target);
                foundedEntry.observer.observe(target);
            }
            else {
                /** @type {?} */
                const root = this.getRootElement(config.root);
                /** @type {?} */
                const options = {
                    root: root !== null ? root : undefined,
                    rootMargin: config.rootMargin,
                    threshold: config.threshold
                };
                /** @type {?} */
                const entry = {
                    root,
                    configHash: config.hash,
                    targets: new Set([target]),
                    observer: new IntersectionObserver((entries) => this.ngZone.run(() => this.emitTrigger(entries)), options)
                };
                entry.observer.observe(target);
                this.registry = [...this.registry, entry];
            }
        });
    }
    /**
     * @param {?} target
     * @param {?} config
     * @return {?}
     */
    unregister(target, config) {
        this.ngZone.runOutsideAngular(() => {
            /** @type {?} */
            const foundedEntry = this.findEntry(config.root, config.hash);
            if (foundedEntry) {
                const { observer, targets } = foundedEntry;
                if (targets.has(target)) {
                    observer.unobserve(target);
                    targets.delete(target);
                }
                if (targets.size === 0) {
                    observer.disconnect();
                    this.registry = this.registry.filter((entry) => entry !== foundedEntry);
                }
            }
        });
    }
}
InViewportService.ɵfac = function InViewportService_Factory(t) { return new (t || InViewportService)(ɵngcc0.ɵɵinject(ɵngcc0.NgZone)); };
InViewportService.ɵprov = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjectable({ token: InViewportService, factory: InViewportService.ɵfac, providedIn: 'root' });
InViewportService.ctorParameters = () => [
    { type: NgZone }
];
/** @nocollapse */ InViewportService.ngInjectableDef = defineInjectable({ factory: function InViewportService_Factory() { return new InViewportService(inject(NgZone)); }, token: InViewportService, providedIn: "root" });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(InViewportService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: ɵngcc0.NgZone }]; }, null); })();

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/** @type {?} */
const InViewportMetadata = Symbol('InViewportMetadata');
class InViewportDirective {
    /**
     * @param {?} platformId
     * @param {?} elementRef
     * @param {?} inViewport
     */
    constructor(platformId, // tslint:disable-line
    elementRef, inViewport) {
        this.platformId = platformId;
        this.elementRef = elementRef;
        this.inViewport = inViewport;
        this.config = new InViewportConfig();
        this.destroyed$ = new Subject();
        this.inViewportAction = new EventEmitter();
    }
    /**
     * @param {?} value
     * @return {?}
     */
    set options(value) {
        this.config = new InViewportConfig(value);
    }
    /**
     * @return {?}
     */
    ngAfterViewInit() {
        if (isPlatformBrowser$1(this.platformId)) {
            this.inViewport.register(this.elementRef.nativeElement, this.config);
            this.inViewport.trigger$
                .pipe(filter((entry) => {
                return entry && entry.target === this.elementRef.nativeElement;
            }), takeUntil(this.destroyed$))
                .subscribe((entry) => {
                this.emitAction(entry, false);
            });
        }
        else {
            this.emitAction(undefined, true);
        }
    }
    /**
     * @return {?}
     */
    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
        if (isPlatformBrowser$1(this.platformId)) {
            this.inViewport.unregister(this.elementRef.nativeElement, this.config);
        }
    }
    /**
     * @private
     * @param {?} entry
     * @param {?} force
     * @return {?}
     */
    check(entry, force) {
        /** @type {?} */
        const isVisible = () => {
            /** @type {?} */
            const partiallyVisible = entry.isIntersecting || entry.intersectionRatio > 0;
            /** @type {?} */
            const completelyVisible = entry.intersectionRatio >= 1;
            return this.config.partial ? partiallyVisible : completelyVisible;
        };
        /** @type {?} */
        const visible = force || !entry || isVisible();
        return { [InViewportMetadata]: { entry }, target: this.elementRef.nativeElement, visible };
    }
    /**
     * @private
     * @param {?} entry
     * @param {?} force
     * @return {?}
     */
    emitAction(entry, force) {
        /** @type {?} */
        const event = this.config.checkFn
            ? this.config.checkFn(entry, { force, config: this.config })
            : this.check(entry, force);
        this.inViewportAction.emit(event);
    }
}
InViewportDirective.ɵfac = function InViewportDirective_Factory(t) { return new (t || InViewportDirective)(ɵngcc0.ɵɵdirectiveInject(PLATFORM_ID), ɵngcc0.ɵɵdirectiveInject(ɵngcc0.ElementRef), ɵngcc0.ɵɵdirectiveInject(InViewportService)); };
InViewportDirective.ɵdir = /*@__PURE__*/ ɵngcc0.ɵɵdefineDirective({ type: InViewportDirective, selectors: [["", "inViewport", ""]], inputs: { options: ["inViewportOptions", "options"] }, outputs: { inViewportAction: "inViewportAction" } });
InViewportDirective.ctorParameters = () => [
    { type: Object, decorators: [{ type: Inject, args: [PLATFORM_ID,] }] },
    { type: ElementRef },
    { type: InViewportService }
];
InViewportDirective.propDecorators = {
    options: [{ type: Input, args: ['inViewportOptions',] }],
    inViewportAction: [{ type: Output }]
};
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(InViewportDirective, [{
        type: Directive,
        args: [{
                selector: '[inViewport]'
            }]
    }], function () { return [{ type: Object, decorators: [{
                type: Inject,
                args: [PLATFORM_ID]
            }] }, { type: ɵngcc0.ElementRef }, { type: InViewportService }]; }, { inViewportAction: [{
            type: Output
        }], options: [{
            type: Input,
            args: ['inViewportOptions']
        }] }); })();

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class InViewportModule {
}
InViewportModule.ɵfac = function InViewportModule_Factory(t) { return new (t || InViewportModule)(); };
InViewportModule.ɵmod = /*@__PURE__*/ ɵngcc0.ɵɵdefineNgModule({ type: InViewportModule });
InViewportModule.ɵinj = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjector({ imports: [CommonModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(InViewportModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule],
                declarations: [InViewportDirective],
                exports: [InViewportDirective]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(InViewportModule, { declarations: function () { return [InViewportDirective]; }, imports: function () { return [CommonModule]; }, exports: function () { return [InViewportDirective]; } }); })();

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */

export { InViewportConfig, InViewportConfigDirection, InViewportDirective, InViewportMetadata, InViewportModule, InViewportService, isPlatformBrowser, toBase64, toBase64Browser, toBase64Node };

